import React, { Fragment, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { Button, Grid, IconButton } from "@material-ui/core";
import DeleteRoundedIcon from "@material-ui/icons/DeleteRounded";
import EditRoundedIcon from "@material-ui/icons/EditRounded";

import ProductCounter from "../../ProductCounter/ProductCounter";
import { setCategoryId } from "../../../Stores/commonSlice";
import { selectDelivery } from "../../../Stores/deliverySlice";
import { selectCart, addToCart } from "../../../Stores/redisCartSlice";
import {
  selectInformation,
  selectMoney,
} from "../../../Stores/informationSlice";
import CustomModal from "../../Modal/CustomModal";
import ModifyOrderNote from "../../Options/ModifyOrderNote/ModifyOrderNote";
import { ICartResponse } from "../../../Models/Cart/ICartResponse";
import { IAttributeCartResponse } from "../../../Models/Cart/IAttributeCartRespons";
import { ICartRequest } from "../../../Models/Cart/ICartRequest";
import { ICart } from "../../../Models/Cart/ICart";
import { CounterMode } from "../../../Models/Enums/counterMode";
import { GetStoreId } from "../../../Services/storeSession";
import agent from "../../../Services/agent";
import Loading from "../../Loading/Loading";
import { history } from "../../..";

import { CartItemStyles } from "./CartItemStyles";

interface IProps {
  cartItem: ICartResponse;
  freebieQuantity?: number;
}

const CartItem: React.FC<IProps> = ({ cartItem, freebieQuantity }) => {
  const classes = CartItemStyles();
  const { t } = useTranslation();

  // =======================useSelector=======================
  const dispatch = useDispatch();
  const cart = useSelector(selectCart);
  const money = useSelector(selectMoney);
  const delivery = useSelector(selectDelivery);
  const information = useSelector(selectInformation);

  // ========================useState=========================
  const [modal, setModal] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);

  // =========================values==========================
  const storeId = GetStoreId();
  const cartId = cart?.userCart?.id ? cart.userCart.id : undefined;
  let requestCart: ICartRequest;
  if (storeId) {
    requestCart = {
      id: cartId,
      storeId: storeId,
      isFreebie: cartItem.isFreebie,
      delivery: delivery?.delivery,
      productId: cartItem.productId,
      duplicateNum: cartItem.duplicateNum,
      quantity: cartItem.quantity,
      note: cartItem.note,
      editable: cartItem.editable,
      postCode: delivery?.postCode,
      attributes: cartItem.attributes,
    };
  } else toast.warn(t("storeNotFound"));

  // =========================methods=========================
  const handleQtyChanged = (quantity: number, counterMode?: CounterMode) => {
    if (
      cartItem.isFreebie &&
      counterMode === CounterMode.Increase &&
      freebieQuantity
    ) {
      const freebiesCartItems = cart?.userCart?.carts.filter(
        (cartItem) => cartItem.isFreebie
      );

      let numberOfFreebiesCartItem = 0;
      freebiesCartItems.map((item) => {
        numberOfFreebiesCartItem += item.quantity;
      });

      if (numberOfFreebiesCartItem >= freebieQuantity) {
        toast.warn(
          t("youJustCanAdd") + freebieQuantity + t("freeProductSToTheCart")
        );
        return;
      }
    }
    if (storeId && requestCart) {
      let model = { ...requestCart };
      model.quantity = quantity;
      handleUpdateCart(model);
    } else toast.error(t("somethingWentWrongTryAgain"));
  };

  const handleNoteChanged = (note: string) => {
    if (storeId && requestCart) {
      let model = { ...requestCart };
      model.note = note;
      handleUpdateCart(model);
    } else toast.error(t("somethingWentWrongTryAgain"));
  };

  const handleUpdateCart = async (model: ICartRequest) => {
    if (information.openOrder || information.preOrder) {
      setLoading(true);
      await agent.Cart.post(model).then((res: ICart) => {
        setLoading(false);
        if (res) {
          dispatch(addToCart(res));
        } else {
          toast.error(t("somethingWentWrongTryAgain"));
        }
      });
    } else toast.warn(t("onlineOrderClosed"));
  };

  if (loading) return <Loading />;
  // ==========================tsx============================
  return (
    <Fragment>
      <CustomModal
        title={t("deleteProduct")}
        description={t("thisWillDeleteSelectedProduct")}
        open={modal}
        handleShow={() => setModal(false)}
        action={
          <Fragment>
            <Button
              onClick={() => setModal(false)}
              variant="contained"
              color="secondary"
            >
              {t("no")}
            </Button>
            <Button
              onClick={() => {
                handleQtyChanged(0);
                setModal(false);
              }}
              variant="contained"
              color="primary"
            >
              {t("yes")}
            </Button>
          </Fragment>
        }
      />
      <div className={classes.cartItem}>
        <div className="mainItem">
          <h6>{cartItem.name}</h6>
          <span
            className={`
            price 
            currency 
            ${
              !!cartItem.discount && cartItem.discount > 0 && classes.discount
            }`}
          >
            {money(cartItem.total)}
          </span>
        </div>

        {!!cartItem.discount && cartItem.discount > 0 && (
          <div className="mainItem">
            <div className={classes.discountTitle}>
              <p>{cartItem.discountText}</p>
            </div>
            <span className={classes.discountPrice}>
              {money(cartItem.discount)}
            </span>
          </div>
        )}

        <ul className="extra">
          {cartItem.attributes
            .slice()
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map((item: IAttributeCartResponse) => (
              <li key={item.id}>
                {item.name}
                <span className="price currency">
                  {item.price === 0
                    ? null
                    : `${money(item.price * cartItem.quantity)}`}
                </span>
              </li>
            ))}
        </ul>
        <Grid container justifyContent="space-between" alignItems="center">
          <div className="qty">
            <div className="cartSubtitle">{t("qty")}</div>
            <ProductCounter
              quantity={cartItem.quantity}
              handleUpdateCart={handleQtyChanged}
            />
          </div>

          {cartItem.note && (
            <ModifyOrderNote
              note={cartItem.note}
              onChange={(note) => {
                if (cartItem) handleNoteChanged(note);
              }}
            />
          )}
          <div className="buttons">
            {cartItem.editable && (
              <IconButton
                onClick={() => {
                  dispatch(setCategoryId({ categoryId: cartItem.categoryId }));
                  const likeEachOther = `/ordering/options/${cartItem.categoryId}/${cartItem.mainProductId}/${cartItem.productId}`;
                  if (
                    history.location.pathname !==
                    `${likeEachOther}/${cartItem.duplicateNum}`
                  ) {
                    window.location.href = `${likeEachOther}/${cartItem.duplicateNum}`;
                  }
                }}
                className="green"
              >
                <EditRoundedIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton onClick={() => setModal(true)} className="red">
              <DeleteRoundedIcon fontSize="small" />
            </IconButton>
          </div>
        </Grid>
      </div>
    </Fragment>
  );
};

export default CartItem;
